/**
 * Simple token storage utility.
 * Handles persistence of authentication tokens in localStorage.
 * Provides event notifications when tokens change.
 */

export const ACCESS_TOKEN_KEY = 'cohaven_access_token';
export const REFRESH_TOKEN_KEY = 'cohaven_refresh_token';

type TokenChangeListener = (hasToken: boolean) => void;

class TokenStorage {
  private listeners: Set<TokenChangeListener> = new Set();

  getAccessToken(): string | null {
    return localStorage.getItem(ACCESS_TOKEN_KEY);
  }

  getRefreshToken(): string | null {
    return localStorage.getItem(REFRESH_TOKEN_KEY);
  }

  setTokens(accessToken: string, refreshToken: string): void {
    localStorage.setItem(ACCESS_TOKEN_KEY, accessToken);
    localStorage.setItem(REFRESH_TOKEN_KEY, refreshToken);
    this.notifyListeners(true);
  }

  clearTokens(): void {
    localStorage.removeItem(ACCESS_TOKEN_KEY);
    localStorage.removeItem(REFRESH_TOKEN_KEY);
    this.notifyListeners(false);
  }

  getAuthorizationHeader(): string | null {
    const token = this.getAccessToken();
    return token ? `Bearer ${token}` : null;
  }

  /**
   * Subscribe to token change events.
   * Returns an unsubscribe function.
   */
  subscribe(listener: TokenChangeListener): () => void {
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
    };
  }

  private notifyListeners(hasToken: boolean): void {
    this.listeners.forEach(listener => {
      try {
        listener(hasToken);
      } catch (error) {
        console.error('Error in token change listener:', error);
      }
    });
  }
}

export const tokenStorage = new TokenStorage();
